// web/src/services/chatService.ts
// Photo-aware chat service — localStorage sessions + backend API for LLM responses.
// Model-agnostic: provider selection happens server-side.

import { WebAuthService } from './auth';

// ─── Types ────────────────────────────────────────────────

export interface PhotoContext {
  fileName: string;
  thumbnailDataUrl: string;
  compositeScore: number;
  dimensions: Record<string, number>;
  dimensionNotes: Record<string, string>;
  detractorPenalty: number;
  detectedFaceCount: number;
  landmarkTier: string;
  editPlan?: { summary: string; weakestDimension?: string; topEdit?: { dimension: string; intensity: number; operation: string } | null } | null;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  photoContext?: PhotoContext[];
  timestamp: number;
}

export interface ChatSession {
  sessionId: string;
  userId: string;
  title: string;
  createdAt: number;
  lastMessageAt: number;
  messageCount: number;
  photoCount: number;
  preview: string;
}

export interface ChatRequest {
  sessionId: string;
  message: string;
  history: { role: string; content: string }[];
  photoContext?: PhotoContext[];
  modelProvider?: string;
  scoringMode?: string;
  prismEnabled?: boolean;
  userId?: string;
  prismContext?: string;
}

// ─── Storage keys ─────────────────────────────────────────

const SESSIONS_KEY = 'frame_chat_sessions';
const messagesKey = (id: string) => `frame_chat_messages_${id}`;

// ─── Helpers ──────────────────────────────────────────────

function uuid(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readJSON<T>(key: string, fallback: T): T {
  try { return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback; } catch { return fallback; }
}

function writeJSON(key: string, value: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(value)); }
  catch (e) { console.error('[FRAME] localStorage write failed:', e); }
}

// ─── Session CRUD ─────────────────────────────────────────

export const ChatService = {
  /** Create a fresh session (called on every login) */
  createSession(userId: string): ChatSession {
    const session: ChatSession = {
      sessionId: uuid(),
      userId,
      title: 'New conversation',
      createdAt: Date.now(),
      lastMessageAt: Date.now(),
      messageCount: 0,
      photoCount: 0,
      preview: '',
    };
    const sessions = this.getSessions(userId);
    sessions.unshift(session);
    writeJSON(SESSIONS_KEY, sessions);
    writeJSON(messagesKey(session.sessionId), []);
    return session;
  },

  /** Get all sessions for a user, sorted newest first */
  getSessions(userId: string): ChatSession[] {
    const all: ChatSession[] = readJSON(SESSIONS_KEY, []);
    return all
      .filter(s => s.userId === userId)
      .sort((a, b) => b.lastMessageAt - a.lastMessageAt);
  },

  /** Load messages for a session */
  getMessages(sessionId: string): ChatMessage[] {
    return readJSON(messagesKey(sessionId), []);
  },

  /** Delete a session and its messages */
  deleteSession(sessionId: string, userId: string): void {
    const sessions = readJSON<ChatSession[]>(SESSIONS_KEY, []);
    writeJSON(SESSIONS_KEY, sessions.filter(s => s.sessionId !== sessionId));
    localStorage.removeItem(messagesKey(sessionId));
  },

  /** Append a message to a session's history and update session metadata.
   *  Strips base64 thumbnailDataUrl from photoContext before persisting
   *  to avoid blowing localStorage quota (~5 MB). */
  _appendMessage(sessionId: string, userId: string, msg: ChatMessage): void {
    const messages = this.getMessages(sessionId);
    // Strip heavy thumbnail data before storing
    const stored: ChatMessage = msg.photoContext
      ? { ...msg, photoContext: msg.photoContext.map(p => ({ ...p, thumbnailDataUrl: '' })) }
      : msg;
    messages.push(stored);
    writeJSON(messagesKey(sessionId), messages);

    // Update session metadata
    const sessions = readJSON<ChatSession[]>(SESSIONS_KEY, []);
    const idx = sessions.findIndex(s => s.sessionId === sessionId);
    if (idx !== -1) {
      sessions[idx].lastMessageAt = msg.timestamp;
      sessions[idx].messageCount = messages.length;
      if (msg.photoContext?.length) {
        sessions[idx].photoCount += msg.photoContext.length;
      }
      // Auto-title from first user message
      if (sessions[idx].title === 'New conversation' && msg.role === 'user') {
        sessions[idx].title = msg.content.slice(0, 60) + (msg.content.length > 60 ? '...' : '');
        sessions[idx].preview = msg.content.slice(0, 100);
      }
      writeJSON(SESSIONS_KEY, sessions);
    }
  },

  /** Send a message and get AI response from backend */
  async sendMessage(request: ChatRequest): Promise<ChatMessage> {
    const userMsg: ChatMessage = {
      id: uuid(),
      role: 'user',
      content: request.message,
      photoContext: request.photoContext,
      timestamp: Date.now(),
    };

    // Get userId from current session
    const user = await WebAuthService.getCurrentUser();
    const userId = user?.userId || 'anonymous';
    this._appendMessage(request.sessionId, userId, userMsg);

    // Build conversation history for API (last 20 messages max)
    const history = this.getMessages(request.sessionId)
      .slice(-20)
      .map(m => ({
        role: m.role,
        content: m.content,
        ...(m.photoContext ? { photoContext: m.photoContext } : {}),
      }));

    // Call backend
    const apiUrl = import.meta.env.VITE_API_URL || '';
    let assistantContent: string;

    // Multi-photo analysis: append grouping instruction for 5+ photos
    const effectiveMessage = (request.photoContext && request.photoContext.length >= 5)
      ? request.message + '\n\n[When analyzing multiple photos, provide a concise 1-2 line assessment for EVERY photo. Group by quality tier (excellent 8+, good 6-8, needs work 4-6, poor <4). Be specific.]'
      : request.message;

    try {
      const session = await WebAuthService.getSession();
      const token = session?.tokens?.idToken?.toString() || '';

      const res = await fetch(`${apiUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          sessionId: request.sessionId,
          message: effectiveMessage,
          history,
          photoContext: request.photoContext?.map(p => ({
            fileName: p.fileName,
            compositeScore: p.compositeScore,
            dimensions: p.dimensions,
            dimensionNotes: p.dimensionNotes,
            detractorPenalty: p.detractorPenalty,
            detectedFaceCount: p.detectedFaceCount,
            landmarkTier: p.landmarkTier,
            editPlan: p.editPlan,
          })),
          modelProvider: request.modelProvider,
          scoringMode: request.scoringMode,
          prismEnabled: request.prismEnabled,
          userId: request.userId,
          prismContext: request.prismContext,
        }),
      });

      if (!res.ok) throw new Error(`Chat API error: ${res.status}`);
      const data = await res.json();
      assistantContent = data.response || 'Sorry, I could not generate a response.';
    } catch (err) {
      console.error('[FRAME Chat] API error:', err);
      assistantContent = 'I\'m having trouble connecting right now. Please try again in a moment.';
    }

    const assistantMsg: ChatMessage = {
      id: uuid(),
      role: 'assistant',
      content: assistantContent,
      timestamp: Date.now(),
    };
    this._appendMessage(request.sessionId, userId, assistantMsg);
    return assistantMsg;
  },
};
